/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React from 'react';
import { ViewModel } from '../../../../core/viewmodel/ViewModel';
import { MaterialDialog } from '../../../../models/material/MaterialDialog';
import { initialEmoji, PickEmojiModel } from '../../../../models/components/PickEmojiModel';
import { MaterialPopover } from '../../../../models/material/MaterialPopover';
import { CreateCourseModel } from '../../../../models/components/CreateCourseModel';
import { SnackbarStore } from '../../../../stores/application/SnackbarStore';
import { StoreProvider } from '../../../../stores/StoreProvider';
import * as H from 'history';
import { CourseService } from '../../../../services/CourseService';
import { EmojiData } from 'emoji-mart';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { AuthStore } from '../../../../stores/application/AuthStore';
import { EventStore } from '../../../../stores/application/EventStore';
import { EventTypes } from '../../../../models/application/EventModel';

export class CreateCourseViewModel extends ViewModel {
	private readonly snackbarStore: SnackbarStore;
	private readonly authStore: AuthStore;
	private readonly eventStore: EventStore;

	private createCourseModel: CreateCourseModel = new CreateCourseModel();
	private courseService: CourseService;

	public dialog: MaterialDialog = new MaterialDialog();
	public pickEmojiModel: PickEmojiModel = new PickEmojiModel();
	public popover: MaterialPopover = new MaterialPopover();

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.snackbarStore = storeProvider.snackbarStore;
		this.courseService = storeProvider.courseService;
		this.authStore = storeProvider.authStore;
		this.eventStore = storeProvider.eventStore;
	}

	public getEmoji(): EmojiData {
		return this.pickEmojiModel.chosenEmoji;
	}

	public setEmoji(emoji: EmojiData): void {
		this.pickEmojiModel.setChosenEmoji(emoji);
	}

	public getCourseName(): string {
		return this.createCourseModel.courseName;
	}

	public setCourseName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		this.createCourseModel.setCourseName(e.target.value);
	}

	public getStartDate(): MaterialUiPickersDate {
		return this.createCourseModel.startDate;
	}

	public setStartDate(date: MaterialUiPickersDate): void {
		this.createCourseModel.setStartDate(date);
	}

	public disableCreateButton(): boolean {
		return this.getCourseName() === '' || this.getStartDate() === null;
	}

	private clearCreateCourseDialog(): void {
		this.pickEmojiModel.setChosenEmoji(initialEmoji);
		this.createCourseModel.setCourseName('');
		this.createCourseModel.setStartDate(new Date());
	}

	public closeCreateDialog(): void {
		this.dialog.closeDialog();
		this.clearCreateCourseDialog();
	}

	public createCourse(emoji: EmojiData, courseName: string, startDate: MaterialUiPickersDate): void | Promise<void> {
		if (!this.authStore.hasAuthToken()) {
			return this.snackbarStore.showError('courses.query_courses.no_valid_authToken');
		}

		const authToken: string | null = this.authStore.getAuthToken();

		return this.courseService
			.createCourse(authToken, emoji, courseName, startDate)
			.then(() => {
				this.snackbarStore.showSuccess('courses.create_course_dialog.course_successfully_created');
				this.eventStore.addEvent(EventTypes.COURSE_CREATED);
				this.closeCreateDialog();
			})
			.catch(() => {
				this.snackbarStore.showError('courses.create_course_dialog.course_unsuccessfully_created');
			});
	}
}
